/**
 * ProbabilityDisplay - Shows the current market probability as a percentage.
 * Designed for beginners with clear visual indicators.
 */

import { memo } from 'react'
import { cn } from '../../lib/utils'

export interface ProbabilityDisplayProps {
  /** Current price (0.01-0.99 representing probability) */
  price: number | null
  /** The market question being predicted */
  question: string
  /** Optional className for the container */
  className?: string
}

/**
 * Displays the current implied probability for a prediction market.
 * Shows a visual progress bar and percentage.
 */
export const ProbabilityDisplay = memo(function ProbabilityDisplay({
  price,
  question,
  className,
}: ProbabilityDisplayProps) {
  // Convert price to percentage (0-100)
  const probability = price !== null ? Math.round(price * 100) : null

  // Determine color based on probability
  const getBarColor = (prob: number): string => {
    if (prob >= 70) return 'bg-green-500'
    if (prob >= 50) return 'bg-yellow-500'
    if (prob >= 30) return 'bg-orange-500'
    return 'bg-red-500'
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card p-4',
        className
      )}
      role="region"
      aria-label="Market probability"
    >
      {/* Question */}
      <h3 className="mb-3 text-sm font-medium text-muted-foreground">
        {question}
      </h3>

      {/* Probability display */}
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold tabular-nums text-foreground">
          {probability !== null ? `${probability}%` : '—'}
        </span>
        <span className="text-sm text-muted-foreground">
          chance of Yes
        </span>
      </div>

      {/* Visual progress bar */}
      <div
        className="mt-3 h-3 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={probability ?? 0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Probability: ${probability ?? 0}%`}
      >
        {probability !== null ? (
          <div
            className={cn(
              'h-full transition-all duration-300 ease-out',
              getBarColor(probability)
            )}
            style={{ width: `${probability}%` }}
          />
        ) : null}
      </div>

      {/* Scale labels */}
      <div className="mt-1 flex justify-between text-xs text-muted-foreground">
        <span>0%</span>
        <span>50%</span>
        <span>100%</span>
      </div>

      {/* Interpretation hint for beginners */}
      {probability !== null ? (
        <p className="mt-3 text-xs text-muted-foreground">
          {probability >= 70
            ? 'The market thinks this is likely to happen.'
            : probability >= 50
              ? 'The market is somewhat uncertain.'
              : probability >= 30
                ? 'The market thinks this is unlikely.'
                : 'The market thinks this is very unlikely.'}
        </p>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">
          No trades yet. Place an order to set the price.
        </p>
      )}
    </div>
  )
})
